import React, { createRef, useEffect, useMemo, useState, useRef } from 'react';
import {
  TableContainer as Container,
  Table as MuiTable,
  TableHead as Head,
  TableBody as Body,
  TableRow as Row,
  useTheme,
  Box,
  Divider,
  InputAdornment,
  Button,
  LinearProgress,
  Typography,
  TableCell
} from '@material-ui/core';
import { GetApp } from '@material-ui/icons';
import Cell from './components/Cells/Cell';
import Cell_address from './components/Cells/Cell_address';
import Cell_Timelogs from './components/Cells/Cell_Timelogs';
import Cell_BatchUpdate from './components/Cells/Cell_BatchUpdate';
import Cell_Tooltip from './components/Cells/Cell_Tooltip';
import { useStyles } from './hooks';
import {
  generateTotals,
  generateExportableData,
  getCellValue,
  filterTableData,
  filterColumns,
  getCellStyles,
  getPageData
} from './utils';
import DebounceTextField from '../DebounceTextField';
import { CloudDownload, Search } from '@material-ui/icons';
import { jsonToExcel } from 'src/utils';
import { CellCheckBox, TblFilter, TblPagination } from './components';
import { cloneDeep, isFunction } from 'lodash';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

function boxShadow(value) {
  return value ? '4px 0 6px rgba(0, 0, 0, 0.1)' : 'none';
}

const cellTypeMap = {
  address: Cell_address,
  timelogs: Cell_Timelogs,
  batchupdate: Cell_BatchUpdate,
  tooltip: Cell_Tooltip,
  A: null, // default, or any other types
  C: null
  // add more types if needed
};

/**
 *
 * @param {dataTableProps} props
 * @returns
 */
const Table = ({
  columns = [],
  data = [],
  rowIDKey = 'id',
  totals = [],
  hideToolbar = false,
  search = { show: false, fields: [], position: 'left', errorMessage: '' },
  showAdvancedFilter = false,
  dataExport = {
    show: false,
    fields: [],
    prepend: [],
    showNo: false,
    includeTotals: false,
    renderStatus: false
  },
  title = '',
  toolbar = {
    left: null,
    right: null
  },
  isLoading = false,
  maxHeight = '100%',
  onCellClick = () => {},
  onCellDoubleClick = () => {},
  onCellEdit = () => {},
  getRowClassName = () => {},
  rootContainerProps = {},
  filterValues: parentFilterValues = null,
  updateFilters: updateParentFilters = null,
  searchQuery: parentSearchQuery = null,
  updateSearchQuery: updateParentSearchQuery = null,
  checkboxSelection = false,
  selectionModel = null,
  onSelectionModelChange = null,
  paginate = true,
  pageSize = 10,
  rowsPerPageOptions = [10, 25, 50, 100],
  showRowNo = false,
  showColumnRightBorder = false,
  showCellRightBorder = false,
  showTableBorder = false,
  rowHeight = 46,
  selectCurrentPage = true,
  stickyChkHeader = true,
  stickyChkCellDense = true,
  prDataExport = false,
  otherSettings = {},
  additionalResult = {},
  pagingCompatible = false,
  loadMore_function = () => {},
  singleClickEdit = false,
  // TODO: move to otherSettings
  ixJCd = '',
  handleReloadTimelogs = () => {}
}) => {
  const theme = useTheme();
  const classNames = useStyles({ rowHeight });
  const params = useParams();

  const dataArray = useMemo(() => {
    return Array.isArray(data) ? data : [];
  }, [data]);

  const isToolbarEmpty =
    !search?.show && !dataExport?.show && !toolbar?.left && !toolbar?.right;

  const tableFilters = columns.filter(col => col?.filter?.allow);
  const cleanedColumns = columns.map(col => {
    const isQrCode = col.render?.type === 'qrCode';

    return {
      ...col,
      editable: isQrCode ? false : col.editable ?? false,
      isEditable: isQrCode ? false : col.isEditable ?? false // keep both in case UI uses either
    };
  });

  const { columns: shownColumns, lastStickyIndex } = filterColumns(
    cleanedColumns
  );

  const [cellRefs, setCellRefs] = useState([]);
  const [scrolled, setScrolled] = useState(false);
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(pageSize);

  const usedSelection = selectionModel || selected;
  const { wf, frontEnd } = useSelector(state => state.bizMeta);
  const showScrollBar = frontEnd?.ui_settings?.showScrollBar ?? false;
  const [searchQuery, setSearchQuery] = useState('');
  const [filterValues, setFilterValues] = useState({});
  const [advancedFilter, setAdvancedFilter] = useState({
    operator: '',
    value: '',
    fields: []
  });

  const [rowRemarks, setRowRemarks] = useState('');

  const selectedData = useMemo(() => {
    const itemsData = dataArray.filter(
      item =>
        usedSelection.includes(item?.['jid']) ||
        usedSelection.includes(item?.[rowIDKey])
    );
    return itemsData.length > 0 ? itemsData : dataArray;
  }, [data, usedSelection, dataArray]);

  const colLeft = useMemo(() => {
    const res = [];

    // Account for sticky columns rendered outside shownColumns
    const checkboxWidth = checkboxSelection ? 50 : 0;
    const rowNoWidth = showRowNo ? 60 : 0;

    let temp = checkboxWidth + rowNoWidth;

    for (const [index, col] of shownColumns.entries()) {
      res.push(col?.sticky && index >= 0 ? temp + 'px' : 'auto');
      temp += col?.width || col?.minWidth || 200;
    }

    return res;
  }, [shownColumns, checkboxSelection, showRowNo]);

  const prevFiltersRef = useRef({});
  const prevDataLengthRef = useRef({});

  const shownData = useMemo(
    () =>
      filterTableData(
        dataArray,
        parentFilterValues || filterValues,
        parentSearchQuery || searchQuery,
        advancedFilter
      ),
    [
      dataArray,
      parentFilterValues,
      filterValues,
      parentSearchQuery,
      searchQuery,
      advancedFilter.fields,
      advancedFilter.operator,
      advancedFilter.value
    ]
  );

  // insert an override totals array in the component then apply here
  const columnsTotals = useMemo(() => {
    const itemsData = shownData.filter(item =>
      usedSelection.includes(item?.['jid']) ||
        usedSelection.includes(item?.[rowIDKey])
    );
    const sourceData = itemsData.length > 0 ? itemsData : shownData;
    // add a parameter in generateTotals, or pass the data to it directly and apply, by default don't override
    return generateTotals(totals, sourceData, otherSettings);
  }, [totals, data, usedSelection, shownData]);

  const maxCol = shownColumns.length;
  const maxRow = shownData.length;

  // Memo for filters only
  const filterSignature = useMemo(() => {
    return JSON.stringify({
      fields: advancedFilter.fields,
      operator: advancedFilter.operator,
      value: advancedFilter.value,
      parentFilterValues,
      filterValues,
      parentSearchQuery,
      searchQuery,
      dt1: additionalResult?.dt1 ?? '',
      dt2: additionalResult?.dt2 ?? ''
    });
  }, [
    advancedFilter.fields,
    advancedFilter.operator,
    advancedFilter.value,
    parentFilterValues,
    filterValues,
    parentSearchQuery,
    searchQuery,
    additionalResult?.dt1,
    additionalResult?.dt2
  ]);

  // Memo for data length only
  const dataLengthSignature = useMemo(() => shownData.length, [
    shownData.length
  ]);

  useEffect(() => {
    const resetDisabled =
      otherSettings?.dashboard?.display?.datagridSettings?.reset_page_onload ===
      false;

    const filtersChanged = prevFiltersRef.current !== filterSignature;
    prevFiltersRef.current = filterSignature;

    if (filtersChanged) {
      setPage(0);
      return; // no need to check length
    }

    const lengthChanged = prevDataLengthRef.current !== dataLengthSignature;
    prevDataLengthRef.current = dataLengthSignature;

    if (lengthChanged && !resetDisabled) {
      setPage(0);
    }
  }, [filterSignature, dataLengthSignature]);

  const pageData = useMemo(
    () => (paginate ? getPageData(shownData, page, rowsPerPage) : shownData),
    [paginate, shownData, page, rowsPerPage]
  );

  useEffect(() => {
    setCellRefs(
      Array.from({ length: maxRow }, () =>
        Array.from({ length: maxCol }, () => createRef())
      )
    );
  }, [maxCol, maxRow]);

  function focusCell(rowIndex, colIndex) {
    if (
      colIndex < 0 ||
      colIndex > maxCol - 1 ||
      rowIndex < 0 ||
      rowIndex > maxRow - 1
    )
      return;

    if (cellRefs[rowIndex][colIndex].current) {
      cellRefs[rowIndex][colIndex].current.focus();
    }
  }

  function updateAllSelected() {
    setSelected(state => {
      const newState =
        state.length === 0
          ? (selectCurrentPage ? pageData : shownData).map(
              item => item[rowIDKey]
            )
          : [];

      if (isFunction(onSelectionModelChange))
        onSelectionModelChange(cloneDeep(newState));

      return newState;
    });
  }

  /**
   *
   * @param {rowIDKey} id
   * @param {boolean} checked
   */
  function updateSelected(id, checked) {
    setSelected(state => {
      const newState = checked
        ? [...state, id]
        : state.filter(item => item !== id);

      if (isFunction(onSelectionModelChange))
        onSelectionModelChange(cloneDeep(newState));

      return newState;
    });
  }

  function updateFilters(name, value) {
    if (isFunction(updateParentFilters)) updateParentFilters(name, value);

    setFilterValues(state => ({ ...state, [name]: value }));
  }

  function updateSearchQuery(event) {
    if (isFunction(updateParentSearchQuery)) updateParentSearchQuery(event);

    setSearchQuery(event.target.value);
  }

  function exportData() {
    jsonToExcel({
      data: [
        {
          data: generateExportableData(selectedData, columns, {
            showNo: dataExport?.showNo || false,
            includeTotals: dataExport?.includeTotals || false,
            totals: columnsTotals,
            renderStatus: dataExport?.renderStatus || false,
            wf: wf
          }),
          options: { skipHeader: true }
        }
      ],
      fileName: title
    });
  }

  function handleChangeAdvanceFilter(data) {
    setAdvancedFilter(data);
  }

  function isValidCssColor(color) {
    const s = new Option().style;
    s.color = '';
    s.color = color;
    return s.color !== '';
  }

  function getRowStyle(row) {
    const style = {};

    // Priority: error > warning > info > success
    if (row?.error || row?.red) {
      return {
        ...style,
        backgroundColor: '#e53935', // strong red
        color: '#fff'
      };
    }

    if (row?.yellow || row?.warning) {
      return {
        ...style,
        backgroundColor: '#FFD700', // gold/yellow
        color: '#000'
      };
    }

    if (row?.info || row?.blue) {
      return {
        ...style,
        backgroundColor: '#2196F3', // blue
        color: '#fff'
      };
    }

    if (row?.success || row?.green) {
      return {
        ...style,
        backgroundColor: '#4CAF50', // green
        color: '#fff'
      };
    }

    if (
      row?.color &&
      typeof row.color === 'string' &&
      isValidCssColor(row.color)
    ) {
      style.backgroundColor = row.color;
      style.color = '#000'; // default text color
    }

    // Default: no special styling
    return style;
  }

  useEffect(() => {
    if (!selectionModel) return;

    if (selectionModel.length !== selected.length) {
      setSelected(items =>
        items.filter(item => selectionModel.includes(item.id))
      );
    }
  }, [selectionModel]);

  useEffect(() => {
    if (prDataExport) {
      exportData();
    }
  }, [prDataExport]);

  return (
    <Box {...rootContainerProps}>
      {hideToolbar === false && !isToolbarEmpty && (
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          p={2}
        >
          <Box display="flex" alignItems="center" gridGap={theme.spacing(1)}>
            {search.show === true && search?.position !== 'right' && (
              <DebounceTextField
                variant="outlined"
                size="small"
                placeholder="Search"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  )
                }}
                value={parentSearchQuery || searchQuery}
                onChange={updateSearchQuery}
              />
            )}
            {toolbar.left}
          </Box>
          <Box display="flex" alignItems="center" gridGap={theme.spacing(1)}>
            {toolbar.right}
            {dataExport.show === true && (
              <Button
                size="small"
                startIcon={<CloudDownload />}
                variant="contained"
                color="primary"
                onClick={exportData}
              >
                Export
              </Button>
            )}
            {search.show === true && search?.position === 'right' && (
              <DebounceTextField
                variant="outlined"
                size="small"
                placeholder="Search"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  )
                }}
                value={parentSearchQuery || searchQuery}
                onChange={updateSearchQuery}
              />
            )}
          </Box>
        </Box>
      )}
      {isLoading && <LinearProgress style={{ height: 2 }} />}
      <Divider />
      {(tableFilters?.length > 0 || showAdvancedFilter) && (
        <>
          <TblFilter
            showAdvancedFilter={showAdvancedFilter}
            advancedFilter={advancedFilter}
            submitAdvFilter={handleChangeAdvanceFilter}
            filters={tableFilters}
            filterValues={parentFilterValues || filterValues}
            updateFilters={updateFilters}
            columns={columns}
            data={dataArray}
          />
          <Divider />
        </>
      )}
      <Container
        // Project Cases 2025-07-0898
        // Description: Showing Slidebar permanently
        // front-end:{ui_settings:{showScrollBar:true/false}} default is false
        className={showScrollBar ? classNames.scrollContainer : ''}
        style={{ maxHeight }}
        onScroll={event => {
          setScrolled(event.target.scrollLeft > 15);
        }}
      >
        <MuiTable
          className={clsx(
            classNames.table,
            classNames.alternatingRow
            // showTableBorder && classNames.tableBorder
          )}
        >
          <Head>
            <Row
              style={{
                position: 'sticky',
                top: 0,
                zIndex: 20
              }}
            >
              {checkboxSelection === true && (
                <CellCheckBox
                  // className={classNames.header}
                  className={
                    stickyChkHeader
                      ? classNames.stickyHeader
                      : classNames.header
                  }
                  id=""
                  checked={usedSelection.length !== 0}
                  indeterminate={
                    usedSelection.length > 0 &&
                    usedSelection.length <
                      (selectCurrentPage ? pageData.length : shownData.length)
                  }
                  updateSelected={updateAllSelected}
                />
              )}
              {showRowNo === true && (
                <TableCell
                  className={classNames.header}
                  align="center"
                  style={{
                    width: 60,
                    minWidth: 60,
                    maxWidth: 60
                  }}
                >
                  #
                </TableCell>
              )}
              {shownColumns.map((col, colIndex) => (
                <TableCell
                  key={col.field}
                  className={clsx(
                    classNames.header,
                    col.headerClassName,
                    showColumnRightBorder && classNames.rightBorder
                  )}
                  style={{
                    width: col?.width || 200,
                    minWidth: col?.width ? col.width : col?.minWidth || 200,
                    textAlign: col?.headerAlign || 'left',
                    position: col.sticky ? 'sticky' : 'static',
                    zIndex: col.sticky ? 15 : 10,
                    left: colLeft[colIndex],
                    boxShadow: boxShadow(
                      colIndex === lastStickyIndex && scrolled
                    )
                  }}
                >
                  {col.headerName}
                </TableCell>
              ))}
            </Row>
          </Head>

          <Body>
            {pageData.map((item, rowIndex) => (
              <Row
                key={item[rowIDKey]}
                className={getRowClassName({
                  id: item[rowIDKey],
                  row: item,
                  columns: shownColumns
                })}
                style={{
                  border: 'none',
                  borderBottom: '1px solid #C4C4C4',
                  ...getRowStyle(item)
                }}
              >
                {checkboxSelection === true && (
                  <CellCheckBox
                    className={
                      stickyChkCellDense
                        ? classNames.stickyCellDense
                        : classNames.cellDense
                    }
                    id={item[rowIDKey]}
                    checked={usedSelection.includes(item[rowIDKey])}
                    updateSelected={updateSelected}
                  />
                )}
                {showRowNo === true && (
                  <TableCell
                    className={classNames.cellDense}
                    align="right"
                    style={{
                      width: 60,
                      minWidth: 60,
                      maxWidth: 60,
                      padding: '0 20px'
                    }}
                  >
                    {rowIndex + 1}
                  </TableCell>
                )}
                {shownColumns.map((col, colIndex) => {
                  const clickable = col?.hasOwnProperty('clickable')
                    ? col.clickable
                    : true;
                  const CellComponent =
                    cellTypeMap[col.cellType?.toLowerCase()] || Cell;
                  const sharedProps = {
                    key: col.field,
                    jid: params?.jid || 0,
                    ixJCd,
                    onCellDoubleClick: clickable ? onCellDoubleClick : () => {},
                    onCellClick: clickable ? onCellClick : () => {},
                    onCellEdit,
                    focusCell,
                    className: clsx(
                      classNames.cellDense,
                      showCellRightBorder && classNames.rightBorder
                    ),
                    align: col?.align || 'left',
                    style: getCellStyles(col, colLeft[colIndex], {
                      boxShadow: boxShadow(
                        colIndex === lastStickyIndex && scrolled
                      ),
                      ...getRowStyle(item)
                    }),
                    col,
                    item,
                    rowIDKey,
                    rowIndex,
                    colIndex,
                    cellRef: cellRefs?.[rowIndex]?.[colIndex],
                    singleClickEdit,
                    otherSettings,
                    prevRemarks: rowRemarks,
                    setPrevRemarks: setRowRemarks,
                    shownColumns,
                    handleReloadTimelogs
                  };

                  return <CellComponent {...sharedProps} />;
                })}
              </Row>
            ))}
            {totals.length > 0 && shownData.length > 0 && (
              <Row
                style={{
                  position: 'sticky',
                  bottom: 0,
                  zIndex: 20
                }}
              >
                {checkboxSelection === true && (
                  <TableCell
                    className={classNames.cellDense}
                    style={{
                      width: 50,
                      minWidth: 50,
                      maxWidth: 50,
                      padding: 0
                    }}
                  />
                )}
                {showRowNo === true && (
                  <TableCell
                    className={classNames.cellDense}
                    style={{
                      width: 60,
                      minWidth: 60,
                      maxWidth: 60,
                      padding: '0 20px'
                    }}
                  />
                )}
                {shownColumns.map((col, colIndex) => (
                  <TableCell
                    key={col.field}
                    align={col?.align || 'left'}
                    className={clsx(classNames.cellDense, col.cellClassName)}
                    style={getCellStyles(col, colLeft[colIndex], {
                      borderTop: '2px solid #C4C4C4',
                      fontWeight: 700,
                      boxShadow: boxShadow(
                        colIndex === lastStickyIndex && scrolled
                      ),
                      paddingLeft: 20,
                      paddingRight: 20
                    })}
                  >
                    {totals.includes(col.field)
                      ? getCellValue({
                          col,
                          row: {},
                          id: 'totals',
                          value: columnsTotals[col.field],
                          field: col.field,
                          hasFocus: false
                        })
                      : ''}
                  </TableCell>
                ))}
              </Row>
            )}
          </Body>
        </MuiTable>
      </Container>
      {shownData.length === 0 && (
        <Box
          height={52}
          display="flex"
          justifyContent="center"
          alignItems="center"
          borderBottom="1px solid #C4C4C4"
        >
          <Typography variant="body2">
            {search?.errorMessage && searchQuery
              ? search?.errorMessage
              : 'No Data'}
          </Typography>
        </Box>
      )}
      {(pagingCompatible || paginate === true) && (
        <Box display="flex" justifyContent="flex-end" alignItems="center" p={1}>
          {pagingCompatible && typeof loadMore_function === 'function' && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<GetApp />}
              onClick={loadMore_function}
            >
              Load More
            </Button>
          )}
          {paginate === true && (
            <Box mr={1}>
              <TblPagination
                totalItems={maxRow}
                page={page}
                setPage={setPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                rowsPerPageOptions={rowsPerPageOptions}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};
export default React.memo(Table);
